import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import SEO from "@/components/seo/SEO";
import {
  GoldButton, GhostButton, Eyebrow, SectionHeading, AmbientOrbs, GlassCard,
} from "@/components/common/ui";
import { CheckCircle, Warning, Bed, Sparkle, ForkKnife, Robot, ChartLineUp } from "@phosphor-icons/react";

const MODULE_ICONS = { reception: Bed, housekeeping: Sparkle, restaurant: ForkKnife, "ai-copilot": Robot, analytics: ChartLineUp };

/**
 * Reusable "Enterprise" landing template — used by Trust, Solutions and
 * Industries pages. Purely presentational, driven by a `data` object.
 *
 * shape:
 *   { eyebrow, title, lead, pillars?:[{title,body}], challenges?:[{title,body}],
 *     benefits?:[{title,body}], commitments?:[{k,v}], moduleSlugs?:[slug],
 *     faq?:[{q,a}], relatedSlugs?:[slug] }
 */
export default function EnterpriseTemplate({ data, path, lang, seoTitle, articles = [], modules = [] }) {
  const { t } = useTranslation();

  const relatedModules = (data.moduleSlugs || [])
    .map((s) => modules.find((m) => m.slug === s))
    .filter(Boolean);

  const relatedArticles = (data.relatedSlugs || [])
    .map((s) => articles.find((a) => a.slug === s))
    .filter(Boolean);

  return (
    <>
      <SEO title={seoTitle || data.title} description={data.lead} path={path} />

      {/* HERO */}
      <section className="relative overflow-hidden py-32 lg:py-44">
        <AmbientOrbs />
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10">
          <Eyebrow>{data.eyebrow}</Eyebrow>
          <motion.h1
            initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, ease: [0.16, 1, 0.3, 1] }}
            className="mt-8 max-w-5xl font-serif text-5xl leading-[1.02] tracking-tight sm:text-6xl md:text-7xl"
            data-testid="enterprise-hero-title"
          >
            {data.title}
          </motion.h1>
          <p className="mt-8 max-w-2xl text-lg text-white/65">{data.lead}</p>
          <div className="mt-12 flex flex-wrap gap-4">
            <GoldButton to={`/${lang}/book-demo`} testId="enterprise-cta-demo">{t("cta.primary")}</GoldButton>
            <GhostButton to={`/${lang}/bht-one`}>{t("cta.secondary")} →</GhostButton>
          </div>
        </div>
      </section>

      {/* PILLARS (trust) */}
      {data.pillars && (
        <section className="relative border-y border-white/[0.06] py-24 lg:py-32">
          <div className="mx-auto max-w-[1400px] px-6 lg:px-10">
            <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-3">
              {data.pillars.map((p, i) => (
                <motion.div
                  key={p.title}
                  initial={{ opacity: 0, y: 24 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-40px" }}
                  transition={{ duration: 0.7, delay: i * 0.05, ease: [0.16, 1, 0.3, 1] }}
                  className="group rounded-2xl border border-white/[0.06] bg-white/[0.02] p-8 transition-colors hover:border-bht-gold/30"
                  data-testid={`enterprise-pillar-${i}`}
                >
                  <div className="font-mono text-[10px] uppercase tracking-widest text-bht-gold">P/{String(i + 1).padStart(2, "0")}</div>
                  <div className="mt-6 font-serif text-2xl group-hover:text-bht-gold">{p.title}</div>
                  <p className="mt-3 text-sm leading-relaxed text-white/60">{p.body}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CHALLENGES (solutions/industries) */}
      {data.challenges && (
        <section className="relative border-t border-white/[0.06] py-24 lg:py-32">
          <div className="mx-auto grid max-w-[1400px] gap-16 px-6 lg:grid-cols-12 lg:px-10">
            <div className="lg:col-span-5">
              <SectionHeading eyebrow="Challenges" title={lang === "nl" ? "Wat er nu misgaat." : "What breaks today."} />
            </div>
            <ul className="space-y-4 lg:col-span-7">
              {data.challenges.map((c, i) => (
                <li key={i} className="group flex items-start gap-4 rounded-xl border border-white/[0.06] bg-white/[0.02] p-6" data-testid={`enterprise-challenge-${i}`}>
                  <Warning size={22} weight="regular" className="mt-1 shrink-0 text-white/50 group-hover:text-bht-gold" />
                  <div>
                    <div className="font-serif text-xl">{c.title}</div>
                    <p className="mt-2 text-sm text-white/60">{c.body}</p>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </section>
      )}

      {/* BENEFITS */}
      {data.benefits && (
        <section className="relative border-t border-white/[0.06] py-24 lg:py-32">
          <div className="mx-auto grid max-w-[1400px] gap-16 px-6 lg:grid-cols-12 lg:px-10">
            <div className="lg:col-span-5">
              <SectionHeading eyebrow="Benefits" title={lang === "nl" ? "Wat BHT One eraan doet." : "What BHT One does about it."} />
            </div>
            <ul className="space-y-4 lg:col-span-7">
              {data.benefits.map((b, i) => (
                <li key={i} className="group flex items-start gap-4 rounded-xl border border-white/[0.06] bg-white/[0.02] p-6" data-testid={`enterprise-benefit-${i}`}>
                  <CheckCircle size={22} weight="regular" className="mt-1 shrink-0 text-bht-gold" />
                  <div>
                    <div className="font-serif text-xl">{b.title}</div>
                    <p className="mt-2 text-sm text-white/60">{b.body}</p>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </section>
      )}

      {/* COMMITMENTS (trust KPIs) */}
      {data.commitments && (
        <section className="relative py-24">
          <div className="mx-auto max-w-[1400px] px-6 lg:px-10">
            <div className="grid gap-5 md:grid-cols-3">
              {data.commitments.map((c, i) => (
                <GlassCard key={i} className="p-8" testId={`enterprise-commit-${i}`}>
                  <div className="font-serif text-5xl leading-none tracking-tight text-bht-gold">{c.k}</div>
                  <div className="eyebrow mt-4">{c.v}</div>
                </GlassCard>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* RELATED MODULES */}
      {relatedModules.length > 0 && (
        <section className="relative border-t border-white/[0.06] py-24">
          <div className="mx-auto max-w-[1400px] px-6 lg:px-10">
            <SectionHeading eyebrow="Relevant modules" title={lang === "nl" ? "De modules die dit oplossen." : "The modules that solve this."} />
            <div className="mt-12 grid gap-5 md:grid-cols-2 lg:grid-cols-4">
              {relatedModules.map((m) => {
                const Icon = MODULE_ICONS[m.slug] || Bed;
                return (
                  <Link
                    key={m.slug}
                    to={`/${lang}/modules/${m.slug}`}
                    className="group rounded-2xl border border-white/[0.06] bg-white/[0.02] p-6 transition-colors hover:border-bht-gold/30"
                    data-testid={`enterprise-module-${m.slug}`}
                  >
                    <Icon size={22} weight="thin" className="text-bht-gold" />
                    <div className="mt-6 font-serif text-lg group-hover:text-bht-gold">{m.title}</div>
                    <p className="mt-2 text-xs text-white/55">{m.desc}</p>
                  </Link>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* FAQ */}
      {data.faq && data.faq.length > 0 && (
        <section className="relative border-t border-white/[0.06] py-24">
          <div className="mx-auto max-w-3xl px-6 lg:px-10">
            <SectionHeading eyebrow="FAQ" title={lang === "nl" ? "Veelgestelde vragen" : "Frequently asked"} />
            <div className="mt-12 space-y-3">
              {data.faq.map((f, i) => (
                <details key={i} className="group rounded-xl border border-white/[0.06] bg-white/[0.02] px-5 py-4 open:bg-white/[0.03]" data-testid={`enterprise-faq-${i}`}>
                  <summary className="cursor-pointer list-none font-serif text-lg text-white [&::-webkit-details-marker]:hidden">
                    <span className="mr-3 inline-block w-6 font-mono text-[11px] tracking-widest text-bht-gold">Q/{String(i + 1).padStart(2, "0")}</span>
                    {f.q}
                  </summary>
                  <p className="mt-3 pl-8 text-sm leading-relaxed text-white/60">{f.a}</p>
                </details>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* RELATED ARTICLES */}
      {relatedArticles.length > 0 && (
        <section className="relative border-t border-white/[0.06] py-24">
          <div className="mx-auto max-w-[1400px] px-6 lg:px-10">
            <SectionHeading eyebrow="Continue reading" title={lang === "nl" ? "Verder lezen" : "Continue reading"} />
            <div className="mt-12 grid gap-5 md:grid-cols-2 lg:grid-cols-3">
              {relatedArticles.map((a) => (
                <Link
                  key={a.slug}
                  to={`/${lang}/knowledge/${a.slug}`}
                  className="group rounded-2xl border border-white/[0.06] bg-white/[0.02] p-6 transition-colors hover:border-bht-gold/30"
                  data-testid={`enterprise-related-${a.slug}`}
                >
                  <span className="font-mono text-[10px] uppercase tracking-widest text-bht-gold">{a.category}</span>
                  <div className="mt-4 font-serif text-xl group-hover:text-bht-gold">{a.title}</div>
                  <p className="mt-2 text-sm text-white/55">{a.excerpt}</p>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA */}
      <section className="relative border-t border-white/[0.06] py-32">
        <div className="mx-auto max-w-3xl px-6 text-center lg:px-10">
          <SectionHeading align="center" eyebrow={t("brand.name")} title={t("home.cta_h")} sub={t("home.cta_sub")} />
          <div className="mt-10 flex flex-wrap justify-center gap-4">
            <GoldButton to={`/${lang}/book-demo`}>{t("cta.primary")}</GoldButton>
            <GhostButton to={`/${lang}/contact`}>{t("nav.contact")} →</GhostButton>
          </div>
        </div>
      </section>
    </>
  );
}
